const asObject = value =>
	value && typeof value === 'object' && !Array.isArray(value) ? value : {};
const asArray = value => (Array.isArray(value) ? value : []);

const getPositiveInteger = value => {
	const number = Number(value);
	return Number.isInteger(number) && number > 0 ? number : undefined;
};

const getTrimmedString = value => {
	if (typeof value !== 'string' && typeof value !== 'number') return;
	const result = String(value).trim();
	return result || undefined;
};

const getFrequencyMatch = order => {
	const diagnostics = asObject(order?.importDiagnostics);
	return asObject(diagnostics.frequencyMatch || diagnostics.frequency_match);
};

const normalizeActivity = value => {
	const activity = asObject(value);
	const code = getTrimmedString(activity.code);
	if (!code) return null;
	return {
		code,
		blocking: activity.blocking === true,
	};
};

const normalizeFrequency = (value, { requireChallenge = false } = {}) => {
	const frequency = asObject(value);
	const frequencyId = getPositiveInteger(frequency.frequencyId);
	const name = getTrimmedString(frequency.name);
	const selectionChallenge = getTrimmedString(frequency.selectionChallenge);
	if (!frequencyId || !name || (requireChallenge && !selectionChallenge))
		return;

	const normalized = {
		frequencyId,
		...(getTrimmedString(frequency.code)
			? { code: getTrimmedString(frequency.code) }
			: {}),
		name,
		...(getPositiveInteger(frequency.intervalCount)
			? { intervalCount: getPositiveInteger(frequency.intervalCount) }
			: {}),
		...(getTrimmedString(frequency.intervalUnit)
			? { intervalUnit: getTrimmedString(frequency.intervalUnit) }
			: {}),
	};
	if (selectionChallenge) normalized.selectionChallenge = selectionChallenge;
	return normalized;
};

const withoutSelectionChallenge = frequency => {
	if (!frequency) return null;
	const canonical = { ...frequency };
	delete canonical.selectionChallenge;
	return canonical;
};

export const getContractFrequencyPresentation = order => {
	const match = getFrequencyMatch(order);
	const source = asObject(match.source);
	const sourceValue = Object.prototype.hasOwnProperty.call(source, 'value')
		? source.value
		: undefined;
	const existing = normalizeFrequency(match.existing) || null;
	const requested = normalizeFrequency(match.requested) || null;
	const assigned = normalizeFrequency(match.assigned) || null;
	const mutable = match.mutable === true;
	const seenChallenges = new Set();
	const candidates = asArray(match.candidates)
		.map(candidate => normalizeFrequency(candidate, { requireChallenge: true }))
		.filter(candidate => {
			if (!candidate || seenChallenges.has(candidate.selectionChallenge))
				return false;
			seenChallenges.add(candidate.selectionChallenge);
			return true;
		});
	const status = getTrimmedString(match.status)?.toLowerCase() || '';
	const strategy = getTrimmedString(match.strategy)?.toLowerCase() || null;
	const canonical = assigned || existing;
	const canConfirmRequested =
		mutable &&
		Boolean(requested) &&
		candidates.some(
			candidate => candidate.frequencyId === requested.frequencyId,
		);

	return {
		hasMatch: Object.keys(match).length > 0,
		status,
		strategy,
		sourceValue,
		existing,
		requested,
		assigned,
		canonical,
		mutable,
		candidates,
		canSelect: mutable && candidates.length > 0,
		canConfirmRequested,
		activity: normalizeActivity(match.activity),
	};
};

export const CONTRACT_FREQUENCY_STATUS_LABELS = {
	resolved: 'Frecuencia resuelta',
	blank_preserved: 'Se conserva la frecuencia actual',
	blank: 'Sin frecuencia informada',
	unknown: 'Frecuencia desconocida',
	ambiguous: 'Frecuencia ambigua',
	inactive: 'Frecuencia inactiva',
	review_required: 'Frecuencia pendiente de validar',
	contract_conflict: 'Frecuencias incompatibles para el contrato',
};

export const getContractFrequencyStatusLabel = status =>
	CONTRACT_FREQUENCY_STATUS_LABELS[String(status || '').toLowerCase()] ||
	'Frecuencia pendiente de revisión';

export const getContractFrequencyReviewMessage = order => {
	const frequency = getContractFrequencyPresentation(order);
	if (frequency.status === 'review_required') {
		if (!frequency.canConfirmRequested) {
			if (frequency.existing?.name && frequency.requested?.name)
				return `El contrato tiene frecuencia ${frequency.existing.name} y el archivo solicita ${frequency.requested.name}, pero este cambio no puede confirmarse en esta importación. Corrige la frecuencia fuera de la importación o valida nuevamente para obtener una autorización vigente.`;
			return 'La frecuencia del contrato difiere de la solicitada, pero el cambio no tiene una autorización vigente. Corrígelo fuera de la importación o valida nuevamente.';
		}
		if (frequency.existing?.name && frequency.requested?.name)
			return `El contrato tiene frecuencia ${frequency.existing.name}. El archivo solicita ${frequency.requested.name}. Confirma el cambio antes de importar.`;
		return 'La frecuencia del contrato difiere de la solicitada en el archivo. Confirma el cambio antes de importar.';
	}
	if (frequency.status === 'unknown') {
		const sourceValue = getTrimmedString(frequency.sourceValue);
		return sourceValue
			? `La frecuencia del archivo no existe en el catálogo contractual. Valor recibido: “${sourceValue}”. Corrígela en el archivo o créala antes de importar.`
			: 'La frecuencia del archivo no existe en el catálogo contractual. Corrígela en el archivo o créala antes de importar.';
	}
	return getContractFrequencyStatusLabel(frequency.status);
};

export const applyContractFrequencySelection = ({ order, option, field }) => {
	if (!order || !field?.id) return order;
	const presentation = getContractFrequencyPresentation(order);
	if (!presentation.mutable) return order;
	const optionId = getPositiveInteger(option?.frequencyId);
	const optionChallenge = getTrimmedString(option?.selectionChallenge);
	const candidate = presentation.candidates.find(
		item =>
			item.frequencyId === optionId &&
			item.selectionChallenge === optionChallenge,
	);
	if (!candidate) return order;

	const diagnostics = asObject(order.importDiagnostics);
	const match = getFrequencyMatch(order);
	const assigned = withoutSelectionChallenge(candidate);
	return {
		...order,
		[field.id]: candidate.frequencyId,
		...(field.name ? { [field.name]: candidate.name } : {}),
		importDiagnostics: {
			...diagnostics,
			needsRevalidation: true,
			frequencyMatch: {
				...match,
				status: 'review_required',
				strategy: 'manual',
				assigned,
			},
		},
	};
};

export const getContractFrequencyReviewSelectionsPayload = selections => {
	const seen = new Set();
	return asArray(selections).reduce((payload, selection) => {
		const challenge = getTrimmedString(selection?.challenge);
		if (!challenge || seen.has(challenge)) return payload;
		seen.add(challenge);
		payload.push({ challenge });
		return payload;
	}, []);
};

export const invalidateContractFrequencyCapabilities = order => {
	const diagnostics = asObject(order?.importDiagnostics);
	const match = getFrequencyMatch(order);
	if (!Object.keys(match).length) return order;
	const invalidateMatch = value => {
		const invalidatedMatch = {
			...value,
			mutable: false,
			candidates: [],
		};
		delete invalidatedMatch.reviewChallenge;
		return invalidatedMatch;
	};
	const invalidatedDiagnostics = {
		...diagnostics,
		frequencyMatch: invalidateMatch(match),
	};
	if (Object.keys(asObject(diagnostics.frequency_match)).length)
		invalidatedDiagnostics.frequency_match = invalidateMatch(
			diagnostics.frequency_match,
		);
	return {
		...order,
		importDiagnostics: invalidatedDiagnostics,
	};
};
